import { DataTypeName } from "./data-types";
import {
  GenericDataType,
  genericPrefixes,
  removeGenericFromType,
} from "./data-types/generic-types";

export enum InspectorKind {
  TEXT = "text",
  ENUM = "enum",
  TOKEN = "token",
  BOOLEAN = "boolean",
  NUMBER = "number",
  CHECKOUT_CONTEXT_PREDICATE = "checkoutContextPredicate",
}

/**
 * The inspector each data type is edited with, keyed by the underlying type so
 * every `Maybe`/`Multiple` decoration of it is handled too.
 */
export const inspectorKindByDataType: Record<
  DataTypeName,
  InspectorKind | null
> = {
  Text: InspectorKind.TEXT,
  Url: InspectorKind.TEXT,
  Enum: InspectorKind.ENUM,
  Token: InspectorKind.TOKEN,
  Boolean: InspectorKind.BOOLEAN,
  Number: InspectorKind.NUMBER,
  Email: null,
  Phone: null,
  Date: null,
  DateTime: null,
  Name: null,
  Metadata: null,
  Object: null,
  Customer: null,
  Product: null,
  "Trigger Product": null,
  "Line Item": null,
  Tracker: null,
  "Cart Context": InspectorKind.CHECKOUT_CONTEXT_PREDICATE,
  "Subscriptions Charge": null,
  "Trackable Summary": null,
  "Review Request": null,
  "Email Attachment": null,
  "Url To Qr Code": null,
  "Shopify Checkout": null,
  "Discount Applications": null,
  "Subscription Cart Line": null,
};

/** Renders, but merchants have no reason to match a condition against a link. */
const typesHiddenFromConditionStep: DataTypeName[] = ["Url"];

/** Whether the condition step can offer a field of this type as a variable. */
export function isConditionableDataType(dataType: GenericDataType): boolean {
  const baseType = removeGenericFromType(dataType);
  return (
    inspectorKindByDataType[baseType] !== null &&
    !typesHiddenFromConditionStep.includes(baseType)
  );
}

export type ConditionExpressionType =
  | "text_match"
  | "number_comparison"
  | "boolean_evaluation"
  | "checkout_context_predicate";

/** The expression each inspector writes; enum and token pickers write text matches. */
export const expressionTypeByInspectorKind: Record<
  InspectorKind,
  ConditionExpressionType
> = {
  [InspectorKind.TEXT]: "text_match",
  [InspectorKind.ENUM]: "text_match",
  [InspectorKind.TOKEN]: "text_match",
  [InspectorKind.BOOLEAN]: "boolean_evaluation",
  [InspectorKind.NUMBER]: "number_comparison",
  [InspectorKind.CHECKOUT_CONTEXT_PREDICATE]: "checkout_context_predicate",
};

export type ConditionShape =
  | {
      kind: "scalar";
      inspectorKind: InspectorKind;
      expressionType: ConditionExpressionType;
    }
  | { kind: "array"; element: ConditionShape };

/**
 * What the condition editor renders for a data type, and so the expression it
 * writes: a leading `Multiple` wraps an `array_predicate` around the element,
 * and any other decoration is edited as its base type. `FieldInspector` renders
 * from this.
 */
export function editorShapeForDataType(
  dataType: GenericDataType,
): ConditionShape | null {
  const kind = inspectorKindByDataType[removeGenericFromType(dataType)];
  if (!kind) {
    return null;
  }
  let shape: ConditionShape = {
    kind: "scalar",
    inspectorKind: kind,
    expressionType: expressionTypeByInspectorKind[kind],
  };
  for (
    let rest: string = dataType;
    rest.startsWith(genericPrefixes.Array);
    rest = rest.slice(genericPrefixes.Array.length)
  ) {
    shape = { kind: "array", element: shape };
  }
  return shape;
}

/** The editor shape of a field the condition step offers. Null when it does not offer it. */
export function conditionShapeForDataType(
  dataType: GenericDataType,
): ConditionShape | null {
  return isConditionableDataType(dataType)
    ? editorShapeForDataType(dataType)
    : null;
}
